import {BitmapText, Graphics, IBitmapTextStyle, Rectangle} from "pixi.js";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {MdHinge} from "../../model/hardware/MdHinges";
import {inches, lineBlueHeavy, lineBlueLight} from "../../const";

// hinge knuckle drawn straddling the leaf's hinge edge (scene units = mm). Matches
// the inert knuckle DoorLeaf draws for non-interactive views.
const HINGE_WIDTH = 1 / 2 * inches;

// padding around the thin knuckle for its click hit area.
const HIT_PAD = 1.5 * inches;

// where the override product-code label sits relative to the hinge edge.
const LABEL_DX = 1 * inches;
const LABEL_DY = 0.75 * inches;

const TEXT_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 40,
};

// the knuckle's placement on the leaf, in leaf-local coords. `edgeX` is the hinge
// edge (0 for a left-hung leaf, leafWidth for a right-hung one); `cy` the knuckle
// centre (the hinge's offsetTop); `hh` its drawn height; `outboard` the direction
// away from the leaf (−1 left, +1 right), used to place the override label clear of
// the door face.
export type HingeGeom = {edgeX: number; cy: number; hh: number; outboard: -1 | 1};

// One selectable hinge on the Locations view. Draws the knuckle (highlighted when
// selected), carries a click-to-select hit area, and — when the hinge has a product
// override — shows that product's code beside it. Geometry is supplied per-draw by
// the view (which owns the leaf/frame layout); this Thing only owns interactivity,
// the selection highlight, and the override label.
export class HingeThing extends DrawFrmThing {
    private elev: ElevationApp;
    private hinge: MdHinge;
    private leaf: "active" | "inactive";
    private geom: () => HingeGeom;
    private onSelect: (additive: boolean) => void;

    private selectedFlag = false;

    private glyph: Graphics;
    private label: BitmapText;

    constructor(props: {
        elev: ElevationApp;
        hinge: MdHinge;
        // which leaf this view shows — selects which per-leaf product override the
        // knuckle's label reflects.
        leaf: "active" | "inactive";
        geom: () => HingeGeom;
        onSelect: (additive: boolean) => void;
    }) {
        super();
        this.elev = props.elev;
        this.hinge = props.hinge;
        this.leaf = props.leaf;
        this.geom = props.geom;
        this.onSelect = props.onSelect;

        this.glyph = new Graphics();
        this.lines.addChild(this.glyph);

        this.label = new BitmapText("", TEXT_STYLE);
        this.lines.addChild(this.label);

        this.makeInteractive({
            target: this.glyph,
            onClick: (e) => this.onSelect(e.ctrlKey || e.metaKey),
        });

        // keep the highlight in sync with the canvas selection.
        this.selectedFlag = this.elev.viewSettings.isSelected(this.selectionId());
        this.trackSubscription(this.elev.viewSettings.selection.subscribe(() => {
            const next = this.elev.viewSettings.isSelected(this.selectionId());
            if (next === this.selectedFlag) return;
            this.selectedFlag = next;
            if (!this.destroyed) this.draw();
        }));

        this.draw();
    }

    private selectionId(): string {
        return `hinge:${this.hinge.id}`;
    }

    // the override product's (truncated) code for this view's leaf, or null when the
    // hinge uses that leaf's default main hinge product.
    private overrideCode(): string | null {
        const pid = this.leaf === "active" ? this.hinge.activeProductOverride : this.hinge.inactiveProductOverride;
        if (pid == null) return null;
        const item = (this.elev.hardwareGroup?.hardware ?? []).find(h => h.product === pid);
        if (!item) return null;
        return item.productCode.slice(0, 6);
    }

    draw() {
        if (this.destroyed) return;

        const g = this.glyph;
        g.clear();

        const {edgeX, cy, hh, outboard} = this.geom();

        const color = this.selectedFlag ? lineBlueLight : lineBlueHeavy;
        const alpha = this.selectedFlag ? 1 : 0.5;

        g.lineStyle(1, color, alpha);
        g.beginFill(color, alpha);
        g.drawRect(edgeX - HINGE_WIDTH / 2, cy - hh / 2, HINGE_WIDTH, hh);
        g.endFill();

        // the thin knuckle is hard to hit, so pad the click area generously.
        g.hitArea = new Rectangle(
            edgeX - HINGE_WIDTH / 2 - HIT_PAD, cy - hh / 2 - HIT_PAD,
            HINGE_WIDTH + 2 * HIT_PAD, hh + 2 * HIT_PAD,
        );

        // flag an overridden hinge with the override product's code, placed outboard
        // (away from the door face). right-align it when it reads leftward.
        const code = this.overrideCode();
        if (code) {
            this.label.visible = true;
            this.label.text = code;
            this.label.tint = color;
            this.label.anchor.set(outboard === -1 ? 1 : 0, 1);
            this.label.position.set(edgeX + outboard * LABEL_DX, cy - LABEL_DY);
        } else {
            this.label.visible = false;
        }
    }
}
